import { User } from 'firebase/auth';
import { Recipe } from '../types/mealPlan';

export type PickerCandidatesResponse = {
  favorites: Recipe[];
  cookbook: Recipe[];
  newForYou: Recipe[]; // catalog recipes; id is the catalog ID
  // Full saved-cookbook size (what "Browse all" opens). Used to hide Browse all
  // when the visible cards already cover the whole cookbook. Optional so older
  // cached responses (pre-field) fall back to showing the action.
  cookbookTotal?: number;
  // How many "New for you" cards the server budgeted for. A section that came
  // back short means the filtered catalog is drained, so "Fresh ideas" is
  // hidden — another spin has nothing new to show. Optional for the same
  // pre-field fallback as cookbookTotal.
  newForYouTarget?: number;
  // Which of the newForYou catalog IDs are on the nightly trending list. A
  // separate list rather than a flag on each recipe: trending is a global
  // property recomputed every night, not something stored on the recipe.
  // Optional for the same pre-field fallback as cookbookTotal.
  trendingCatalogRecipeIds?: string[];
  // Retrieval source per newForYou catalog ID ("base" | "exploration" |
  // "prep" | "trending" | "backfill") — attached to analytics so exploration
  // share can be monitored. Optional for the same pre-field fallback.
  newForYouSources?: Record<string, string>;
};

// Wizard-held cache so back/forward navigation doesn't refetch; keyed by the
// plan start date, which is what changes the seasonal seeding
export type PickerCandidatesCache = {
  key: string;
  data: PickerCandidatesResponse;
};

export type PickerRequestInput = {
  dateRange: [string | null, string | null];
  uniqueMeals?: number;
  // "Tell us how you like to meal prep" free text — tints the server's
  // meal-prep slice retrieval when meal prep is on
  mealPrepPreferences?: string;
};

// Everything the shortlist request depends on, resolved once so the wizard's
// prefetch and the picker step derive an identical cache key. They must agree:
// a key that disagrees by so much as a trimmed space turns a warm cache into a
// second round trip on the step that was supposed to open instantly.
export type PickerRequest = {
  cacheKey: string;
  dateRange: [string | null, string | null];
  uniqueMeals?: number;
  totalDays: number;
  mealPrepEnabled: boolean;
  prepNotes: string;
};

export const buildPickerRequest = ({
  dateRange,
  uniqueMeals,
  mealPrepPreferences,
}: PickerRequestInput): PickerRequest => {
  let totalDays = 7;
  if (dateRange[0] && dateRange[1]) {
    const diffTime = Math.abs(
      new Date(dateRange[1]).getTime() - new Date(dateRange[0]).getTime()
    );
    totalDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  }
  // Meal prep reserves a slice of "New for you" for prep-friendly recipes
  // server-side (retrieved in the user's stated prep style), so both are
  // part of the cache key: going back and changing either must refetch,
  // not serve the other mode's shortlist
  const mealPrepEnabled = uniqueMeals !== undefined && uniqueMeals < totalDays;
  const prepNotes = mealPrepEnabled ? mealPrepPreferences?.trim() ?? '' : '';
  const cacheKey = `${dateRange[0] ?? 'no-date'}|prep:${
    mealPrepEnabled ? uniqueMeals : 'off'
  }|notes:${prepNotes}`;
  return { cacheKey, dateRange, uniqueMeals, totalDays, mealPrepEnabled, prepNotes };
};

export const buildPickerQuery = (
  request: PickerRequest,
  excludeIds?: string[]
): string => {
  const params = new URLSearchParams();
  if (request.dateRange[0]) params.set('startDate', request.dateRange[0]);
  if (request.mealPrepEnabled) {
    params.set('uniqueMeals', String(request.uniqueMeals));
    params.set('totalDays', String(request.totalDays));
    if (request.prepNotes) params.set('mealPrepPreferences', request.prepNotes);
  }
  if (excludeIds?.length) params.set('excludeIds', excludeIds.join(','));
  return params.toString();
};

export const requestPickerCandidates = async (
  user: User,
  request: PickerRequest,
  excludeIds?: string[]
): Promise<PickerCandidatesResponse> => {
  const token = await user.getIdToken();
  const query = buildPickerQuery(request, excludeIds);
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/meals/picker-candidates${
      query ? `?${query}` : ''
    }`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!response.ok) throw new Error(`Request failed: ${response.status}`);
  return (await response.json()) as PickerCandidatesResponse;
};

// In-flight requests by cache key. The wizard warms the shortlist a step
// early, so the picker step routinely mounts while that request is still
// open — without this it would fire a second identical one and the warm-up
// would have bought nothing. Entries clear on settle, so a later visit with
// the same key still refetches (the wizard's own cache decides that).
const inFlight = new Map<string, Promise<PickerCandidatesResponse>>();

/**
 * Fetch a picker shortlist, joining an identical request already in flight.
 * Rejections propagate to every caller; both call sites handle them.
 */
export const fetchPickerCandidates = (
  user: User,
  request: PickerRequest
): Promise<PickerCandidatesResponse> => {
  const existing = inFlight.get(request.cacheKey);
  if (existing) return existing;
  const pending: Promise<PickerCandidatesResponse> = requestPickerCandidates(
    user,
    request
  ).finally(() => {
    if (inFlight.get(request.cacheKey) === pending) {
      inFlight.delete(request.cacheKey);
    }
  });
  inFlight.set(request.cacheKey, pending);
  return pending;
};
